import { isLocalStorageSupported } from '@zillow/is-local-storage-supported';
import { isNull } from 'src/utils/typeguards/isNull';
import { isUndefined } from 'src/utils/typeguards/isUndefined';

/**
 * Returns an item from local storage if it exists and local storage is supported
 */
export const getRawLocalStorageItem = (storageKey: string) => {
    if (isLocalStorageSupported()) {
        return globalThis.localStorage.getItem(storageKey);
    }
};

/**
 * Returns an item, JSON parsed, from local storage if it exists and local storage is supported
 */
export const getParsedLocalStorageItem = <TExpectedReturnType = unknown>(storageKey: string) => {
    try {
        const unparsed = getRawLocalStorageItem(storageKey);
        if (isNull(unparsed) || isUndefined(unparsed)) {
            return unparsed;
        }
        return JSON.parse(unparsed) as TExpectedReturnType;
    } catch {} // eslint-disable-line no-empty
};

/**
 * Sets an item in local storage if local storage is supported
 */
export const setLocalStorageItem = <TItemType>(storageKey: string, item: TItemType) => {
    if (isLocalStorageSupported()) {
        globalThis.localStorage.setItem(storageKey, JSON.stringify(item));
    }
};

/**
 * Sets an item in local storage if local storage is supported
 */
export const setRawLocalStorageItem = (storageKey: string, item: string) => {
    if (isLocalStorageSupported()) {
        globalThis.localStorage.setItem(storageKey, item);
    }
};

/**
 * Deletes an item from local storage if local storage is supported
 */
export const deleteLocalStorageItem = (storageKey: string) => {
    if (isLocalStorageSupported()) {
        globalThis.localStorage.removeItem(storageKey);
    }
};

type ItemWithExpiry<TExpectedReturnType = unknown> = {
    item: TExpectedReturnType;
    expiryMillisecondTimestamp: number;
};

/**
 * Sets an item in local storage with a TTL as a datetime in ms if local storage is supported
 */
export const setLocalStorageItemWithExpiry = <TItemType>(storageKey: string, item: TItemType, ttlInMS: number) => {
    const storedItem: ItemWithExpiry<typeof item> = {
        expiryMillisecondTimestamp: Date.now() + ttlInMS,
        item,
    };

    setLocalStorageItem(storageKey, storedItem);
};

/**
 * Gets an item, JSON parsed, from local storage if local storage is supported and the item TTL hasn't expired
 */
export const getParsedLocalStorageItemWithExpiry = <TExpectedReturnType = unknown>(storageKey: string) => {
    const data = getParsedLocalStorageItem<ItemWithExpiry<TExpectedReturnType>>(storageKey);
    if (!data) {
        return null;
    }

    const { item, expiryMillisecondTimestamp } = data;

    if (Date.now() > expiryMillisecondTimestamp) {
        deleteLocalStorageItem(storageKey);
        return null;
    }

    return item;
};
